import { datadogRum } from '@datadog/browser-rum';

type BuildRoutes = Record<string, string>;

type CachedBuildRoutes = {
  timestamp: number;
  data: BuildRoutes;
};

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const RETRY_DELAYS_MS = [1000, 2000] as const;
const buildsMemoryCache = new Map<string, BuildRoutes>();
const inFlightRequests = new Map<string, Promise<BuildRoutes>>();

const getStagePrefix = (educationLevel: string): string => {
  switch (educationLevel) {
    case 'PRIMARIA':
    case 'PRIMARY':
      return 'Primary';
    case 'SECUNDARIA':
    case 'SECONDARY':
      return 'Secondary';
    default:
      return 'Primary';
  }
};

const getCacheKey = (stagePrefix: string): string =>
  `routes-builds:${stagePrefix}`;

const getLocalStorageInstance = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const loadCachedBuildRoutes = (key: string): CachedBuildRoutes | null => {
  const storage = getLocalStorageInstance();

  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(key);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as CachedBuildRoutes;
  } catch {
    storage.removeItem(key);
    return null;
  }
};

const persistBuildRoutes = ({
  key,
  data,
}: {
  key: string;
  data: BuildRoutes;
}): void => {
  const storage = getLocalStorageInstance();

  if (!storage) {
    return;
  }

  const payload: CachedBuildRoutes = {
    timestamp: Date.now(),
    data,
  };

  try {
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    storage.removeItem(key);
  }
};

const isCacheFresh = (timestamp: number): boolean => {
  return Date.now() - timestamp < ONE_DAY_IN_MS;
};

const wait = ({ duration }: { duration: number }): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, duration);
  });
};

const fetchBuildRoutes = async ({
  url,
}: {
  url: string;
}): Promise<BuildRoutes> => {
  let attempt = 0;
  let lastError: unknown = null;

  while (attempt <= RETRY_DELAYS_MS.length) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      return (await response.json()) as BuildRoutes;
    } catch (error) {
      lastError = error;

      if (attempt >= RETRY_DELAYS_MS.length) {
        break;
      }

      const delay = RETRY_DELAYS_MS[attempt];
      attempt += 1;
      await wait({ duration: delay });
    }
  }

  if (lastError instanceof Error) {
    throw lastError;
  }

  throw new Error(`Error fetching routes.json. ${url}`);
};

const fetchBuilds = async (
  educationalLevel: string,
  unityStandaloneBuildDomain: string
): Promise<BuildRoutes> => {
  const stagePrefix = getStagePrefix(educationalLevel);
  const cacheKey = getCacheKey(stagePrefix);

  const cachedBuildRoutes = buildsMemoryCache.get(cacheKey);

  if (cachedBuildRoutes) {
    return cachedBuildRoutes;
  }

  const cachedEntry = loadCachedBuildRoutes(cacheKey);

  if (cachedEntry && isCacheFresh(cachedEntry.timestamp)) {
    buildsMemoryCache.set(cacheKey, cachedEntry.data);
    return cachedEntry.data;
  }

  const existingRequest = inFlightRequests.get(cacheKey);

  if (existingRequest) {
    return existingRequest;
  }

  const buildsUrl = `${unityStandaloneBuildDomain}/${stagePrefix}/BuildRoutes/routes.json`;

  const request = (async () => {
    try {
      const fetchedBuildRoutes = await fetchBuildRoutes({ url: buildsUrl });
      buildsMemoryCache.set(cacheKey, fetchedBuildRoutes);
      persistBuildRoutes({ key: cacheKey, data: fetchedBuildRoutes });
      return fetchedBuildRoutes;
    } catch (error) {
      const fallbackBuildRoutes = cachedEntry?.data;
      const errorInstance =
        error instanceof Error
          ? error
          : new Error(`Error fetching routes.json. ${buildsUrl}`);

      if (fallbackBuildRoutes) {
        buildsMemoryCache.set(cacheKey, fallbackBuildRoutes);
        console.error(errorInstance);
        errorInstance.name = 'RoutesJsonError';
        datadogRum.addError(errorInstance, {
          errorMessage: errorInstance.message,
          buildsUrl,
          hasLocalFallback: true,
        });
        return fallbackBuildRoutes;
      }

      console.error(errorInstance);
      errorInstance.name = 'RoutesJsonError';
      datadogRum.addError(errorInstance, {
        errorMessage: errorInstance.message,
        buildsUrl,
        hasLocalFallback: false,
      });
      throw errorInstance;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, request);
  return request;
};

export const resolveBuildPath = async (
  sceneName: string,
  educationalLevel: string,
  unityBuild: string,
  unityStandaloneBuildDomain: string
): Promise<string> => {
  const buildRoutes = await fetchBuilds(
    educationalLevel,
    unityStandaloneBuildDomain
  );
  const stagePrefix = getStagePrefix(educationalLevel);

  if (sceneName && buildRoutes[sceneName]) {
    return `${unityStandaloneBuildDomain}/${stagePrefix}${buildRoutes[sceneName]}`;
  }
  return unityStandaloneBuildDomain;
};
